import React from "react";
import { useVerifyOtp } from "./hooks/useVerifyOtp";
import { InputLoader } from "@/components/ui/InputLoader";
import { assets } from "@/utils/assets";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";

export const VerifyOtpPage = () => {
  const { t } = useTranslation();
  const wallpapers = assets.loginWallpapers;
  const [wallpaperIndex, setWallpaperIndex] = React.useState(0);
  const currentWallpaper = wallpapers[wallpaperIndex % wallpapers.length];

  React.useEffect(() => {
    if (wallpapers.length <= 1) return;
    const intervalId = setInterval(() => {
      setWallpaperIndex((prev) => (prev + 1) % wallpapers.length);
    }, 8000);
    return () => clearInterval(intervalId);
  }, [wallpapers.length]);

  const {
    code,
    phone,
    isLoading,
    errorMessage,
    successMessage,
    resendDisabled,
    onCodeChange,
    onSubmit,
    handleResendCode,
  } = useVerifyOtp();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="flex min-h-screen flex-col lg:h-full lg:flex-row">
      <div className="flex w-full flex-1 flex-col justify-center items-center bg-white px-6 py-10 sm:px-10 lg:w-1/2 lg:px-8 lg:py-0 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="mx-auto w-full max-w-md lg:w-1/2">
          <img
            src={assets.logo}
            alt="logo"
            className="mx-auto mb-8 h-20 lg:h-18 lg:mb-19"
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">
                {t("otp.title")}
              </h1>
              <p className="mt-2 text-sm text-gray-600">{t("otp.subtitle")}</p>
              {phone && (
                <p className="mt-1 text-sm text-gray-500 font-medium">
                  {phone}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Verification Code
              </label>
              <input
                type="text"
                id="code"
                name="code"
                maxLength={4}
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  onCodeChange(value);
                }}
                className="w-full px-4 py-3 text-center text-2xl font-semibold tracking-widest border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="0000"
                autoComplete="one-time-code"
              />
            </div>

            {errorMessage && (
              <div className="text-red-500 text-sm">{errorMessage}</div>
            )}

            {successMessage && (
              <div className="text-green-500 text-sm">{successMessage}</div>
            )}

            <button
              type="submit"
              disabled={isLoading || code.length !== 4}
              className="mt-8 w-full rounded-full bg-primary px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300 lg:mt-10 lg:w-60 lg:h-15 lg:rounded-4xl lg:block lg:mx-auto"
            >
              {isLoading ? <InputLoader /> : t("otp.continue")}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendDisabled || isLoading}
                className="text-sm font-semibold text-primary hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {t("otp.resendCode")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="hidden items-center justify-center lg:flex lg:w-1/2">
        <div className="relative h-full w-full">
          <img
            key={currentWallpaper}
            src={currentWallpaper}
            alt="otp wallpaper"
            className="h-full w-full object-cover transition-opacity duration-700 ease-in-out"
          />
        </div>
      </div>
    </div>
  );
};
